import {
  Typography,
  IconButton,
  CircularProgress,
  Box,
  Stack,
  Grid,
  SvgIcon,
} from '@mui/material';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { getContent } from 'apis/content';
import { useDispatch, useSelector } from 'react-redux';
import { setAppbarMenu, setTitle } from 'store/layout';
import { useCallback, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { selectMe } from 'store/auth';
import ShareIcon from '@mui/icons-material/Share';
import { ReactComponent as HeartIcon } from 'static/icons/heart.svg';
import { ReactComponent as HeartBoldIcon } from 'static/icons/heart-bold.svg';
import { ReactComponent as FavoriteIcon } from 'static/icons/archive-book.svg';
import { ReactComponent as FavoriteBoldIcon } from 'static/icons/archive-book-bold.svg';
import { KeyboardBackspace, MoreVert } from '@mui/icons-material';
import { FAVORITE, LIKE } from 'constants';
import InteractionButton from '../Content/components/InteractionButton/InteractionButton';
import ForwardButton from '../Content/components/ForwardButton/ForwardButton';
import ElectButton from '../Content/components/ElectButton/ElectButton';
import MyReactPlayer from '../../components/MyReactPlayer/MyReactPlayer';
import { isAudio, isVideo } from '../../utils';
import ContentPreview from '../../components/ContentPreview/ContentPreview';
import MyAvatar from '../../components/MyAvatar/MyAvatar';
import Description from '../Content/components/Description/Description';
import AppbarMenu from './components/AppbarMenu/AppbarMenu';

export async function loader({ params }) {
  return params.contentId;
}

function getInteraction(interactions, interactionType) {
  return interactions.find(i => i.type === interactionType);
}

export default function ContentReels() {
  const contentId = useLoaderData();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch post data
  const fetchPost = async id => {
    const response = await getContent(id);
    return response.data;
  };

  // Fetch initial data when the component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      const postData = await fetchPost(contentId);
      setPosts([postData]); // Start with the current post
      setCurrentIndex(0); // Set index for the current post
      setLoading(false); // Set loading to false after fetching
    };
    loadInitialData();
  }, [contentId]);

  // Preload next/previous posts
  useEffect(() => {
    if (posts.length > 10) {
      return;
    }
    if (posts.length && currentIndex !== null) {
      // Preload next post
      fetchPost(posts[currentIndex].id + 1).then(post => {
        setPosts(prevPosts => [...prevPosts, post]);
      });
      // Preload previous post
      fetchPost(posts[currentIndex].id - 1).then(post => {
        setPosts(prevPosts => [post, ...prevPosts]);
      });
    }
  }, [currentIndex, posts]);

  const handleNext = useCallback(() => {
    if (currentIndex < posts.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, posts.length]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  // Swipe handlers
  const handlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrev(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  useEffect(() => () => dispatch(setAppbarMenu(null)), []);

  const isVideoOrAudio =
    isVideo(posts[currentIndex]?.file_type) ||
    isAudio(posts[currentIndex]?.file_type) ||
    false;

  return (
    <Grid
      container
      {...handlers}
      className="carousel-container"
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <div
          className="post"
          style={{ transition: 'transform 0.5s ease-in-out' }}
        >
          <Typography
            sx={{
              display: 'flex',
              position: 'absolute',
              top: 10,
              right: 10,
              cursor: 'pointer',
            }}
            onClick={() => {
              navigate(`/content/${contentId}`);
            }}
          >
            بازگشت
            <KeyboardBackspace style={{ marginRight: 3 }} />
          </Typography>
          {/* <Typography variant="h5">{posts[currentIndex].title}</Typography> */}
          {/* <Typography variant="body1"> */}
          {/*  {posts[currentIndex].description} */}
          {/* </Typography> */}
          {isVideoOrAudio ? (
            <MyReactPlayer
              url={posts[currentIndex].file}
              isAudio={isAudio(posts[currentIndex].file_type)}
            />
          ) : (
            <img
              src={posts[currentIndex].file}
              alt={posts[currentIndex].title}
              style={{ width: '100%', height: 'auto' }}
            />
          )}

          <Stack
            direction="column"
            style={{
              position: 'absolute',
              bottom: 0,
              width: '100%',
              padding: 15,
            }}
          >
            <Box style={{ paddingLeft: 70 }}>
              <MyAvatar
                avatar={posts[currentIndex].author.avatar}
                fullName={
                  !!posts[currentIndex]?.author?.first_name ||
                  !!posts[currentIndex]?.author?.last_name
                    ? `${posts[currentIndex]?.author?.first_name} ${posts[currentIndex]?.author?.last_name}`
                    : 'بدون نام'
                }
                sx={{ mt: 8 }}
                username={posts[currentIndex].author.display_username}
                userId={posts[currentIndex].author.id}
                withLink
              />
            </Box>
            <Box style={{ paddingLeft: 70 }}>
              <Description dsc={posts[currentIndex].description} />
            </Box>
          </Stack>

          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'absolute',
              bottom: 0,
              left: 0,
              padding: 10,
              // backgroundColor: 'red',
            }}
          >
            <InteractionButton
              text={posts[currentIndex].favorites_count}
              icon={FavoriteIcon}
              filledIcon={FavoriteBoldIcon}
              type={FAVORITE}
              interaction={getInteraction(
                posts[currentIndex].interactions,
                FAVORITE,
              )}
              contentId={contentId}
              authRequired
              iconColor="white"
              textColor="white"
              flexDirection="column"
              iconSize="large"
            />
            <InteractionButton
              text={posts[currentIndex].likes_count}
              icon={HeartIcon}
              filledIcon={HeartBoldIcon}
              type={LIKE}
              interaction={getInteraction(
                posts[currentIndex].interactions,
                LIKE,
              )}
              contentId={contentId}
              authRequired
              iconColor="white"
              textColor="white"
              flexDirection="column"
              iconSize="large"
            />

            <ForwardButton
              contentId={contentId}
              iconColor="white"
              iconSize="large"
            />
            <MoreVert
              fontSize="large"
              style={{ marginTop: 8, marginBottom: 8 }}
            />
          </Box>
        </div>
      )}
    </Grid>
  );
}
